import React, { useEffect, useState } from 'react';
import { Settings2, Plus, Trash2 } from 'lucide-react';
import ConditionEditor from './ConditionEditor';
import { FormField, FieldType, ValidationRule, DataSource } from './types';
import { dangerIconButton, inputClass, secondaryButton, subtleButton, helpClass } from './ui';
import { DataSourceEditor, defaultDataSource } from './field/DataSourceEditor';
import ConditionBlock from './field/ConditionBlock';
import { Field, SectionTitle, Empty } from './field/parts';

export { DataSourceEditor };

const types: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' }, { value: 'textarea', label: 'Long text' }, { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' }, { value: 'password', label: 'Password' }, { value: 'url', label: 'URL' },
  { value: 'tel', label: 'Telephone' }, { value: 'date', label: 'Date' }, { value: 'time', label: 'Time' },
  { value: 'datetime', label: 'Date & time' }, { value: 'select', label: 'Select' }, { value: 'multi_select', label: 'Multi-select' },
  { value: 'radio', label: 'Radio' }, { value: 'checkbox', label: 'Checkbox' }, { value: 'checkbox_group', label: 'Checkbox group' },
  { value: 'file', label: 'File upload' }, { value: 'hidden', label: 'Hidden' }, { value: 'dynamic_group', label: 'Dynamic fields group (API)' },
];
const ruleTypes: { value: ValidationRule['type']; label: string }[] =
  ['required', 'min_length', 'max_length', 'min', 'max', 'regex', 'email', 'number', 'integer', 'one_of', 'file_extension', 'file_size']
    .map((x) => ({ value: x as ValidationRule['type'], label: x.replace('_', ' ') }));

export default function FieldEditor({
  field,
  allKeys,
  onChange,
  onApplyOrder,
  fieldCount,
}: {
  field: FormField;
  allKeys: { key: string; label: string; type: FieldType }[];
  onChange: (f: FormField) => void;
  onApplyOrder?: (order: number) => void;
  fieldCount?: number;
}) {
  const patch = (p: Partial<FormField>) => onChange({ ...field, ...p });
  const [orderDraft, setOrderDraft] = useState(String(field.order + 1));
  const [keyError, setKeyError] = useState<string>();
  useEffect(() => {
    setOrderDraft(String(field.order + 1));
  }, [field.key, field.order]);
  const handleKeyChange = (nextKey: string) => {
    const normalized = nextKey.trim();
    if (!normalized) {
      setKeyError('Field key is required.');
      return;
    }
    if (allKeys.some((item) => item.key === normalized && item.key !== field.key)) {
      setKeyError(`Field key "${normalized}" is already used.`);
      return;
    }
    setKeyError(undefined);
    onChange({ ...field, key: normalized });
  };
  const applyOrder = () => {
    const parsed = Number(orderDraft);
    if (!Number.isFinite(parsed)) {
      setOrderDraft(String(field.order + 1));
      return;
    }
    const nextOrder = Math.min(Math.max(Math.round(parsed), 1), Math.max(1, fieldCount ?? allKeys.length));
    setOrderDraft(String(nextOrder));
    onApplyOrder?.(nextOrder - 1);
  };
  const setRule = (i: number, p: Partial<ValidationRule>) => {
    const next = [...field.validations];
    next[i] = { ...next[i], ...p };
    patch({ validations: next });
  };
  const isChoice = ['select', 'multi_select', 'radio', 'checkbox_group'].includes(field.type);

  return (
    <div className="min-h-full">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600">
              <Settings2 className="h-3.5 w-3.5" /> Field configuration
            </div>
            <h2 className="mt-1 truncate text-[15px] font-semibold text-slate-900">{field.label || 'Untitled field'}</h2>
            <div className="mt-0.5 truncate text-[10px] text-slate-400"><span>{field.label || 'Untitled field'}</span><span className="mx-1">·</span><span className="font-mono">{field.key}</span></div>
          </div>
          <span className="rounded-md bg-primary-50 px-2 py-1 text-[10px] font-semibold text-primary-700">#{field.order + 1}</span>
        </div>
      </div>

      <div className="divide-y divide-slate-100 px-4">
        <section className="py-4">
          <SectionTitle title="Basics" description="Identity, presentation and defaults" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Field key">
              <input disabled className={`${inputClass()} ${keyError ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`} value={field.key} onChange={(e) => handleKeyChange(e.target.value)} />
              {keyError && <p className="mt-1 text-[10px] text-red-600" role="alert">{keyError}</p>}
            </Field>
            <Field label="Label"><input className={inputClass()} value={field.label} onChange={(e) => patch({ label: e.target.value })} /></Field>
            <Field label="Field type">
              <div className="flex gap-1.5">
                <select
                  className={inputClass()}
                  value={field.type}
                  onChange={(e) => {
                    const nextType = e.target.value as FieldType;
                    const nextIsChoice = ['select', 'multi_select', 'radio', 'checkbox_group'].includes(nextType);
                    const nextDataSource = field.data_source
                      ? {
                          ...field.data_source,
                          response: {
                            ...field.data_source.response,
                            type: (nextIsChoice ? 'options' : field.data_source.response.type === 'options' ? 'value' : field.data_source.response.type) as DataSource['response']['type'],
                          },
                        }
                      : field.data_source;
                    patch({ type: nextType, data_source: nextDataSource });
                  }}
                >
                  {types.filter((t) => t.value !== 'dynamic_group').map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </Field>
            <Field label="Order">
              <div className="flex gap-1.5">
                <input className={`${inputClass()} min-w-0`} type="number" min={1} max={Math.max(1, fieldCount ?? allKeys.length)} value={orderDraft} onChange={(e) => setOrderDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyOrder(); } }} />
                <button type="button" className={secondaryButton} onClick={applyOrder} disabled={Number(orderDraft) === field.order + 1}>Apply</button>
              </div>
              <p className={helpClass}>Apply to move this field in the builder and preview.</p>
            </Field>
          </div>
          <Field label="Placeholder"><input className={inputClass()} value={field.placeholder || ''} onChange={(e) => patch({ placeholder: e.target.value })} /></Field>
          <Field label="Help text"><textarea className={inputClass()} rows={2} value={field.help_text || ''} onChange={(e) => patch({ help_text: e.target.value })} /></Field>
          <Field label="Default value"><input className={inputClass()} value={field.default_value == null ? '' : String(field.default_value)} onChange={(e) => patch({ default_value: e.target.value })} /></Field>
          <label className="mt-3 flex cursor-pointer items-start gap-2 text-[12px] font-medium text-slate-700">
            <input className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" type="checkbox" checked={field.show_in_review !== false} onChange={(e) => patch({ show_in_review: e.target.checked })} />
            <span>
              Show in final review
              <span className={`${helpClass} block font-normal`}>
                {field.type === 'dynamic_group' ? 'Applies to every field generated by this group.' : 'Display this field’s value on the review step before submitting.'}
              </span>
            </span>
          </label>
        </section>

        {field.type === 'number' && (
          <section className="py-4">
            <SectionTitle title="Number settings" description="Control range and increment" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Minimum"><input className={inputClass()} type="number" value={field.min ?? ''} onChange={(e) => patch({ min: e.target.value === '' ? undefined : Number(e.target.value) })} /></Field>
              <Field label="Maximum"><input className={inputClass()} type="number" value={field.max ?? ''} onChange={(e) => patch({ max: e.target.value === '' ? undefined : Number(e.target.value) })} /></Field>
              <Field label="Step"><input className={inputClass()} type="number" value={field.step ?? ''} onChange={(e) => patch({ step: e.target.value === '' ? undefined : Number(e.target.value) })} /></Field>
            </div>
          </section>
        )}

        {isChoice && (
          <section className="py-4">
            <SectionTitle title="Options" description="Define fixed choices here, or use the API data source below for dynamic choices." />
            <div className="space-y-2">
              {field.options.map((o, i) => (
                <div className="grid grid-cols-[1fr_1fr_32px] gap-1.5" key={i}>
                  <input className={inputClass()} placeholder="Value" value={o.value} onChange={(e) => { const x = [...field.options]; x[i] = { ...o, value: e.target.value }; patch({ options: x }); }} />
                  <input className={inputClass()} placeholder="Label" value={o.label} onChange={(e) => { const x = [...field.options]; x[i] = { ...o, label: e.target.value }; patch({ options: x }); }} />
                  <button className={dangerIconButton} type="button" aria-label="Remove option" onClick={() => patch({ options: field.options.filter((_, j) => j !== i) })}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <button type="button" className={subtleButton} onClick={() => patch({ options: [...field.options, { value: `option_${field.options.length + 1}`, label: `Option ${field.options.length + 1}` }] })}><Plus className="h-3.5 w-3.5" /> Add option</button>
            </div>
          </section>
        )}

        <section className="py-4">
          <SectionTitle title="API data source" description={field.type === 'dynamic_group' ? 'Generate runtime child fields from an API response using current form values.' : 'Populate this field or its choices from an API using current form values.'} />
          {!field.data_source ? (
            <button type="button" className={secondaryButton} onClick={() => patch({ data_source: defaultDataSource(field) })}>
              <Plus className="h-3.5 w-3.5" /> Add API data source
            </button>
          ) : (
            <DataSourceEditor field={field} allKeys={allKeys} value={field.data_source} onChange={(value) => patch({ data_source: value })} />
          )}
        </section>

        {field.type === 'file' && (
          <section className="py-4">
            <SectionTitle title="File upload" description="Accepted types and quantity" />
            <Field label="Accepted extensions / MIME types"><input className={inputClass()} value={field.accept.join(',')} placeholder=".csv, image/*" onChange={(e) => patch({ accept: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} /></Field>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-[12px] font-medium text-slate-700">
              <input className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" type="checkbox" checked={field.multiple} onChange={(e) => patch({ multiple: e.target.checked })} />
              Allow multiple files
            </label>
          </section>
        )}

        <section className="py-4">
          <SectionTitle
            title="Validation rules"
            description="Applied when this field is visible"
            action={
              <div className="flex items-center gap-1.5">
                <button type="button" className={secondaryButton} onClick={() => patch({ validations: [...field.validations, { type: 'required', value: true }] })}><Plus className="h-3.5 w-3.5" /> Add rule</button>
              </div>
            }
          />
          {field.validations.length === 0 && <Empty text="No validation rules configured." />}
          <div className="space-y-2">
            {field.validations.map((r, i) => (
              <div className="grid grid-cols-[1fr_0.8fr_1.2fr_32px] gap-1.5" key={i}>
                <select className={inputClass()} value={r.type} onChange={(e) => setRule(i, { type: e.target.value as ValidationRule['type'] })}>{ruleTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
                <input className={inputClass()} placeholder="Value" value={r.value == null ? '' : String(r.value)} onChange={(e) => setRule(i, { value: e.target.value })} />
                <input className={inputClass()} placeholder="Custom message" value={r.message || ''} onChange={(e) => setRule(i, { message: e.target.value })} />
                <button className={dangerIconButton} type="button" aria-label="Remove rule" onClick={() => patch({ validations: field.validations.filter((_, j) => j !== i) })}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </section>

        <section className="py-4">
          <SectionTitle title="Conditional behaviour" description="Use presence when the value itself should not matter." />
          <ConditionBlock title="Visible when" value={field.visible_when} fields={allKeys} onChange={(v) => patch({ visible_when: v })} />
          <ConditionBlock title="Required when" value={field.required_when} fields={allKeys} onChange={(v) => patch({ required_when: v })} />
          <ConditionBlock title="Disabled when" value={field.disabled_when} fields={allKeys} onChange={(v) => patch({ disabled_when: v })} />
        </section>

        <section className="py-4">
          <SectionTitle
            title="Reset rules"
            description="Reset selected fields when a field changes or a condition becomes true."
            action={
              <div className="flex items-center gap-1.5">
                <button type="button" className={secondaryButton} onClick={() => patch({ reset_rules: [...field.reset_rules, { when: { field: allKeys[0]?.key || field.key, operator: 'changed' }, fields: [] }] })}><Plus className="h-3.5 w-3.5" />Add</button>
              </div>
            }
          />
          {field.reset_rules.length === 0 && <Empty text="No reset rules configured." />}
          <div className="space-y-2.5">
            {field.reset_rules.map((r, i) => (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" key={i}>
                <div className="mb-2 text-[11px] font-semibold text-slate-600">When</div>
                <ConditionEditor fields={allKeys} value={r.when} onChange={(v) => {
                  const x = [...field.reset_rules];
                  if (v) x[i] = { ...r, when: v }; else x.splice(i, 1);
                  patch({ reset_rules: x });
                }} />
                <div className="mt-3">
                  <div className="mb-1.5 text-[11px] font-semibold text-slate-600">Fields to reset</div>
                  <div className="space-y-1.5">
                    {(r.fields.length ? r.fields : ['']).map((target, j) => (
                      <div className="grid grid-cols-[1fr_32px] gap-1.5" key={`${i}-${j}`}>
                        <select className={inputClass()} value={target} onChange={(e) => {
                          const x = [...field.reset_rules];
                          const next = [...x[i].fields];
                          if (e.target.value) next[j] = e.target.value; else next.splice(j, 1);
                          x[i] = { ...x[i], fields: Array.from(new Set(next.filter(Boolean))) };
                          patch({ reset_rules: x });
                        }}>
                          <option value="">Select a field…</option>
                          {allKeys.map((k) => <option key={k.key} value={k.key} disabled={k.key === field.key}>{k.label || k.key} · {k.key} · {k.type}</option>)}
                        </select>
                        <button className={dangerIconButton} type="button" aria-label="Remove reset target" onClick={() => {
                          const x = [...field.reset_rules];
                          x[i] = { ...x[i], fields: x[i].fields.filter((_, idx) => idx !== j) };
                          patch({ reset_rules: x });
                        }}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <button type="button" className={subtleButton} onClick={() => {
                    const x = [...field.reset_rules];
                    x[i] = { ...x[i], fields: [...x[i].fields, ''] };
                    patch({ reset_rules: x });
                  }}><Plus className="h-3.5 w-3.5" /> Add field to reset</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
